import {Clickable, Grid, Icon, type ThemeColorPath} from 'platform/foundation';
import {match} from 'ts-pattern';

import {type KeyboardEvent, use, useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {CHART_SUMMARY_COLUMNS} from '../../constants/CHART_SUMMARY_COLUMNS';
import {ChartCardContext} from '../../contexts/ChartCardContext';
import type {ChartPaletteHue} from '../../types/ChartPaletteHue';
import {ChartSummaryCard} from '../ChartSummaryCard/ChartSummaryCard';

// A shown series keeps a grey card, so only its border and dot carry the series colour (Figma
// 49372:6221). A hovered card turns a step darker in both states.
const SHOWN_SERIES_BACKGROUND: ChartPaletteHue['summary']['background'] = 'palettes.neutral.10.100';
const HIDDEN_SERIES_BACKGROUND: ChartPaletteHue['summary']['background'] = 'general.white';
const HOVERED_BACKGROUND: ChartPaletteHue['summary']['background'] = 'palettes.neutral.20.100';
const HIDDEN_SERIES_BORDER: ChartPaletteHue['summary']['border'] = 'general.separator';
const HIDDEN_INDICATOR_COLOR: ThemeColorPath = 'text.tertiary';
// 12 px, the size of the state dot in the design.
const INDICATOR_SIZE = 3;

// A drag that selects a value to copy it ends with a click. That click must not switch the series.
const isSelectingTextIn = (card: HTMLElement) => {
  const selection = window.getSelection();
  return isNotNil(selection) && !selection.isCollapsed && card.contains(selection.anchorNode);
};

interface ChartSummaryToggleItem {
  /** Series id, the value that the chart uses to show or hide the series. */
  id: string;
  label: string;
  /** Formatted summary of the series, for example its total for the selected period. */
  value: string;
  /** Formatted rate shown in a flag after the value, for example its share of the total. */
  rate?: string;
  /** Formatted companion figure under the value, for example the stock card count of a bucket. */
  detail?: string;
  /**
   * Hue of the series, usually a palette hue. While the series shows, `base` fills the state dot
   * and `summary.border` borders the card. Without a hue, the item is a static grey card for a
   * value that is no series of the chart.
   */
  hue?: Pick<ChartPaletteHue, 'base' | 'summary'>;
}

interface ChartSummaryTogglesProps {
  items: ChartSummaryToggleItem[];
  selectedIds: string[];
  onChange: (selectedIds: string[]) => void;
  /** Shows one series at a time: a click selects that series instead of toggling it. */
  isSingleSelect?: boolean;
}

/**
 * Summary cards of the chart series that also show and hide each series. The card of a shown
 * series is grey with the border of its hue and a dot in the series colour. The card of a hidden
 * series is white with an empty circle.
 */
export function ChartSummaryToggles(props: ChartSummaryTogglesProps) {
  const cardWidth = use(ChartCardContext)?.width ?? 'full';
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const toggle = (id: string) =>
    props.onChange(
      match({
        isSingleSelect: Boolean(props.isSingleSelect),
        isSelected: props.selectedIds.includes(id),
      })
        .with({isSingleSelect: true}, () => [id])
        .with({isSelected: true}, () => props.selectedIds.filter((selectedId) => selectedId !== id))
        .otherwise(() => [...props.selectedIds, id])
    );

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>, id: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggle(id);
    }
  };

  return (
    <Grid columns={CHART_SUMMARY_COLUMNS[cardWidth]} spacing={4}>
      {props.items.map((item) => {
        if (isNil(item.hue)) {
          return (
            <ChartSummaryCard
              key={item.id}
              label={item.label}
              value={item.value}
              rate={item.rate}
              detail={item.detail}
            />
          );
        }

        const isSelected = props.selectedIds.includes(item.id);
        const background = match({isHovered: hoveredId === item.id, isSelected})
          .with({isHovered: true}, () => HOVERED_BACKGROUND)
          .with({isSelected: true}, () => SHOWN_SERIES_BACKGROUND)
          .otherwise(() => HIDDEN_SERIES_BACKGROUND);
        return (
          <Clickable
            key={item.id}
            role="button"
            tabIndex={0}
            aria-pressed={isSelected}
            isTextSelectable
            onClick={(event) => {
              if (!isSelectingTextIn(event.currentTarget)) {
                toggle(item.id);
              }
            }}
            onKeyDown={(event) => handleKeyDown(event, item.id)}
            onMouseEnter={() => setHoveredId(item.id)}
            onMouseLeave={() => setHoveredId(null)}
          >
            <ChartSummaryCard
              label={item.label}
              value={item.value}
              rate={item.rate}
              detail={item.detail}
              colors={{
                background,
                border: isSelected ? item.hue.summary.border : HIDDEN_SERIES_BORDER,
              }}
              indicator={
                isSelected ? (
                  <Icon value="image/lens" color={item.hue.base} size={INDICATOR_SIZE} />
                ) : (
                  <Icon
                    value="toggle/radio_button_unchecked"
                    color={HIDDEN_INDICATOR_COLOR}
                    size={INDICATOR_SIZE}
                  />
                )
              }
            />
          </Clickable>
        );
      })}
    </Grid>
  );
}
